import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import StockPerformance from './pages/StockPerformance';
import COInstance from './pages/COInstance';
import DataWarehouse from './pages/DataWarehouse';
import TrackingSystem from './pages/TrackingSystem';
import TicketSystem from './pages/TicketSystem';
import COFeedback from './pages/COFeedback';
import ResolutionMatrices from './pages/ResolutionMatrices';
import Projects from './pages/Projects';
import Inventories from './pages/Inventories';
import UCCInventories from './pages/UCCInventories';
import TemperatureRTMDs from './pages/TemperatureRTMDs';
import Gaps from './pages/Gaps';
import FSP4All from './pages/FSP4All';
import FSP4AllForecasting from './pages/FSP4AllForecasting';
import DRIVE from './pages/DRIVE';
import SCIP from './pages/SCIP';
import SCIPTool from './pages/SCIPTool';
import SCIPToolDetail from './pages/SCIPToolDetail';
import EVMA from './pages/EVMA';
import NLWG from './pages/NLWG';
import Solarization from './pages/Solarization';
import WasteManagement from './pages/WasteManagement';
import CarbonFootprint from './pages/CarbonFootprint';
import CoverageData from './pages/CoverageData';
import GuestTicketPage from './pages/GuestTicketPage';
import NewVaccineIntro from './pages/NewVaccineIntro';
import Outbreaks from './pages/Outbreaks';
import Budgets from './pages/Budgets';
import Admin from './pages/Admin';
import SupportChatbotV2 from './components/SupportChatbotV2';
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import * as Sentry from '@sentry/react';
import ReactGA from 'react-ga4';
import authService from './services/authService';
import userService from './services/userService';
import { UserContext } from './context/UserContext';
import TicketPage from './pages/TicketPage';
import GAVIReportingIndicators from './pages/GAVIReportingIndicators';
import WebV2Module from './modules/webv2/WebV2Module';
import UserEngagementTracker from './components/UserEngagementTracker';


Sentry.init({
  dsn: 'https://5418b63017195aaba7403c963834d757@o4508057773277184.ingest.de.sentry.io/4509688899436624',
  sendDefaultPii: true,
});

// Initialize Google Analytics
ReactGA.initialize('G-5LHLTC7YLF');

function AuthenticatedApp({ setUser }) {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [isInitialized, setIsInitialized] = useState(false);

  // Add state for country picker modal
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [countryModalUserClaims, setCountryModalUserClaims] = useState(null);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [userClaims, setUserClaims] = useState({ email: '', name: '', lastName: '', country: '' });
  const [userAccess, setUserAccess] = useState({ hasAccess: false, isApproved: false, message: '' });
  const [countryModalSelection, setCountryModalSelection] = useState('');
  const [organizationSelection, setOrganizationSelection] = useState('');
  const [otherOrganizationName, setOtherOrganizationName] = useState('');
  const [titleInput, setTitleInput] = useState('');
  const [showTitleModal, setShowTitleModal] = useState(false);
  const [titlePromptInput, setTitlePromptInput] = useState('');
  const [titlePromptSubmitting, setTitlePromptSubmitting] = useState(false);
  const accessCheckedRef = React.useRef(false);
  const loginRedirectStartedRef = React.useRef(false);
  const [accessChecked, setAccessChecked] = useState(false);

  // Initialize MSAL
  useEffect(() => {
    const initializeMsal = async () => {
      try {
        await authService.initialize();
        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to initialize MSAL:', error);
      }
    };
    initializeMsal();
  }, []);

  // Extract user claims and check access (only once per session)
  useEffect(() => {
    if (!isAuthenticated || inProgress !== 'none' || !isInitialized) return;

    (async () => {
      const claims = await authService.extractUserClaims(instance, inProgress);
      setUserClaims(claims);                         // context

      // Only check current access here. New-user creation is handled in
      // the onboarding flow to ensure organization is always collected.
      const access = await userService.checkUserAccess(claims.email);
      setUserAccess(access);
      accessCheckedRef.current = true;
      setAccessChecked(true);
    })();
  }, [isAuthenticated, inProgress, isInitialized, instance]);

  // Reset access check ref on logout
  useEffect(() => {
    if (!isAuthenticated) {
      accessCheckedRef.current = false;
      setAccessChecked(false);
      setShowTitleModal(false);
      setTitlePromptInput('');
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isInitialized || isAuthenticated || inProgress !== 'none' || loginRedirectStartedRef.current) return;

    const hashParams = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
    const hasAuthResponse =
      hashParams.has('code') ||
      hashParams.has('error') ||
      hashParams.has('error_description');

    if (hasAuthResponse) return;

    loginRedirectStartedRef.current = true;
    authService.loginRedirect(instance, inProgress).catch((error) => {
      loginRedirectStartedRef.current = false;
      console.error('Login redirect failed:', error);
    });
  }, [isInitialized, isAuthenticated, inProgress, instance]);

  // Keep GA4 user properties in sync for country/organization reporting.
  useEffect(() => {
    if (!isAuthenticated) return;

    const dbUser = userAccess?.user || {};
    const country = String(dbUser.country || userClaims.country || '').trim();
    const organization = String(dbUser.organization || '').trim();
    const userId = String(dbUser.id || '').trim();

    if (!country && !organization && !userId) return;

    try {
      if (userId) {
        ReactGA.set({ user_id: userId });
      }
      ReactGA.gtag('set', 'user_properties', {
        country: country || '(not set)',
        organization: organization || '(not set)'
      });
    } catch (error) {
      console.error('Failed to set GA user properties:', error);
    }
  }, [isAuthenticated, userAccess, userClaims]);

  // Fetch available countries for the modal
  useEffect(() => {
    if (showCountryModal) {
      setCountryModalSelection(countryModalUserClaims?.country || '');
      setOrganizationSelection('');
      setOtherOrganizationName('');
      setTitleInput('');
      userService.getAvailableCountries().then(setAvailableCountries);
    }
  }, [showCountryModal, countryModalUserClaims]);

  // Handle B2C password reset
  useEffect(() => {
    if (isInitialized) {
      authService.handlePasswordReset(instance);
    }
  }, [instance, isInitialized]);

  // Ensure user is in DB after login/signup (only once per session)
  const userCreatedRef = React.useRef(false);
  useEffect(() => {
    if (isAuthenticated && userClaims.email && !userCreatedRef.current && isInitialized) {
      userCreatedRef.current = true;
      (async () => {
        try {
          const access = await userService.checkUserAccess(userClaims.email);
          const hasUserRecord =
            access.hasAccess ||
            access.isApproved ||
            /pending|rejected|deactivated/i.test(String(access.message || ''));

          if (hasUserRecord) {
            setUserAccess(access);
            if (setUser && access && access.user) {
              setUser(access.user);
            }
            return;
          }

          // New user: if country is already in claims, preselect it in modal
          // and still require organization before account creation.
          if (String(userClaims.country || '').trim()) {
            setCountryModalUserClaims(userClaims);
            setShowCountryModal(true);
            return;
          }

          await authService.ensureUserInDatabase(userClaims, (claims) => {
            setCountryModalUserClaims(claims);
            setShowCountryModal(true);
          });

          const refreshedAccess = await userService.checkUserAccess(userClaims.email);
          setUserAccess(refreshedAccess);
          if (setUser && refreshedAccess && refreshedAccess.user) {
            setUser(refreshedAccess.user);
          }
        } catch (_) {}
      })();
    }
  }, [isAuthenticated, userClaims, isInitialized, setUser]);

  // Prompt existing users to complete a missing job title after login.
  useEffect(() => {
    if (!accessChecked || showCountryModal) return;
    if (!userAccess?.hasAccess || !userAccess?.user?.id) {
      setShowTitleModal(false);
      return;
    }
    const hasTitle = String(userAccess.user.title || '').trim().length > 0;
    setShowTitleModal(!hasTitle);
    if (!hasTitle) {
      setTitlePromptInput('');
    }
  }, [accessChecked, userAccess, showCountryModal]);

  const handleTitleModalSubmit = async () => {
    const finalTitle = String(titlePromptInput || '').trim();
    if (!finalTitle || titlePromptSubmitting || !userClaims.email) return;

    setTitlePromptSubmitting(true);
    try {
      await userService.updateUserTitle(finalTitle);
      const access = await userService.checkUserAccess(userClaims.email);
      setUserAccess(access);
      if (setUser && access?.user) {
        setUser(access.user);
      } else if (setUser) {
        setUser({ ...userAccess.user, title: finalTitle });
      }
      setShowTitleModal(false);
    } catch (err) {
      console.error('Error saving job title:', err);
    } finally {
      setTitlePromptSubmitting(false);
    }
  };

  // Handle country modal submit
  const handleCountryModalSubmit = async (
    selectedCountry,
    selectedAccessLevel = 'country',
    selectedOrganization = '',
    enteredOtherOrganization = '',
    enteredTitle = ''
  ) => {
    if (!countryModalUserClaims) return;
    if (!selectedCountry || !selectedOrganization) return;
    if (selectedOrganization === 'Other' && !String(enteredOtherOrganization || '').trim()) return;
    const finalTitle = String(enteredTitle || '').trim();
    if (!finalTitle) return;

    const finalOrganization = selectedOrganization === 'Other'
      ? String(enteredOtherOrganization || '').trim()
      : selectedOrganization;

    try {
      ReactGA.gtag('set', 'user_properties', {
        country: selectedCountry || '(not set)',
        organization: finalOrganization || '(not set)'
      });
      ReactGA.gtag('event', 'onboarding_profile_submitted', {
        country: selectedCountry,
        organization: finalOrganization
      });
    } catch (_) {}

    setShowCountryModal(false);
    const updatedClaims = {
      ...countryModalUserClaims,
      country: selectedCountry,
      accessLevel: selectedAccessLevel,
      organization: finalOrganization,
      title: finalTitle
    };
    try {
      await authService.ensureUserInDatabase(updatedClaims);
      // Trigger a new access check
      const access = await userService.checkUserAccess(updatedClaims.email);
      setUserAccess(access);
      // Update global user context with the DB user if available; fallback to updated claims
      if (setUser) {
        if (access && access.user) {
          setUser(access.user);
        } else {
          setUser(updatedClaims);
        }
      }
    } catch (err) {
      if (err?.message?.includes('Invalid country') || err?.message?.includes('Country is required')) {
        setCountryModalUserClaims(updatedClaims);
        setShowCountryModal(true);
      } else {
        console.error('Error creating user:', err);
      }
    }
  };

  // Add sign out button for authenticated users
  const handleLogout = async () => {
    if (isInitialized) {
      await authService.logoutRedirect(instance);
    }
  };

  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Initializing...</div>
      </div>
    );
  }

  // Handle B2C forgot password redirect immediately (before early returns)
  if (!isAuthenticated) {
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    const hasForgotError = hash && hash.includes('AADB2C90118');
    if (hasForgotError) {
      authService.handlePasswordReset(instance);
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-xl">Redirecting to password reset...</div>
        </div>
      );
    }
  }

  if (inProgress === 'login') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Authenticating...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Redirecting to login...</div>
      </div>
    );
  }

  // before any gated screen, show a brief access check spinner
  if (!accessChecked) {
    return (
      <div className="flex items-center justify-center min-h-screen">
      </div>
    );
  }

  // Show pending approval screen only if explicitly denied access
  const pendingStatuses = ['pending', 'rejected', 'deactivated'];
  const isExplicitlyDenied =
    userAccess?.message &&
    !userAccess.hasAccess &&
    pendingStatuses.some(s => userAccess.message.toLowerCase().includes(s));

  if (isExplicitlyDenied) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md text-center">
          <div className="mb-6">
            <i className="fa-solid fa-clock text-6xl text-yellow-500 mb-4"></i>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Access Pending</h2>
            <p className="text-gray-600 mb-4">
              {userAccess.message || 'Your account is pending approval by an administrator. Contact thrive36oTeam@unicef.org. / Votre compte est en attente d\'approbation par un administrateur. Contactez thrive36oTeam@unicef.org.'}
            </p>
            <p className="text-sm text-gray-500">
              You will be notified via email once your access is approved. Contact thrive360Team@unicef.org if you have any questions.
              <br />
              Vous serez informe(e) par email une fois votre acces approuve. Contactez thrive360Team@unicef.org si vous avez des questions.
            </p>
          </div>
          <button
            onClick={handleLogout}
            className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  // HARD GUARD: do not render app routes unless the user actually has access
  if (!userAccess.hasAccess && !showCountryModal) {
    return (
      <div className="flex items-center justify-center min-h-screen">
      </div>
    );
  }

  if (showTitleModal) {
    const canSubmitTitle = String(titlePromptInput || '').trim().length > 0;

    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Complete your profile</h2>
          <p className="mb-6 text-gray-600 text-left">
            Please add your job title to continue using THRIVE360.
            <br />
            <span className="text-sm text-gray-500">
              Veuillez indiquer votre titre de poste pour continuer.
            </span>
          </p>
          <div className="mb-6 text-left">
            <label className="block mb-2 font-semibold text-gray-700">Job title / Titre du poste</label>
            <input
              type="text"
              className="w-full p-2 border border-gray-300 rounded-md"
              value={titlePromptInput}
              onChange={e => setTitlePromptInput(e.target.value)}
              placeholder="e.g. Immunization Officer / ex. Agent de vaccination"
              required
              autoFocus
            />
          </div>
          <button
            type="button"
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-semibold py-2 px-4 rounded-md"
            disabled={!canSubmitTitle || titlePromptSubmitting}
            onClick={handleTitleModalSubmit}
          >
            {titlePromptSubmitting ? 'Saving...' : 'Continue'}
          </button>
        </div>
      </div>
    );
  }

  // Section-based access helper
  const canAccess = (section) => {
    const accessLevel = userAccess?.user?.accessLevel || userAccess?.accessLevel;
    if (accessLevel === 'admin') return true;
    // Allow country-admins to access the admin section (admin panel)
    if (section === 'admin' && accessLevel === 'country-admin') return true;
    // Allow global users with Azure role 'admin' to access the admin section
    if (section === 'admin' && accessLevel === 'global' && String(userClaims?.role || '').toLowerCase() === 'admin') return true;
    const allowed = Array.isArray(userAccess?.user?.allowedSections)
      ? userAccess.user.allowedSections
      : (Array.isArray(userAccess?.allowedSections) ? userAccess.allowedSections : []);
    // Handle legacy alias for co-instance (plural stored historically)
    if (section === 'co-instance') {
      return allowed.includes('co-instance') || allowed.includes('co-instances');
    }
    return allowed.includes(section);
  };

  const canAccessManagementKpi = () => {
    const accessLevel = String(userAccess?.user?.accessLevel || userAccess?.accessLevel || '').toLowerCase();
    return accessLevel === 'admin' || accessLevel === 'global';
  };

  const guard = (section, element) => (canAccess(section) ? element : <Navigate to="/co-instance" replace />);
  const guardManagementKpi = (element) => {
    if (!canAccessManagementKpi()) {
      return <Navigate to={canAccess('stock-performance') ? '/v2' : '/co-instance'} replace />;
    }
    return canAccess('stock-performance') ? element : <Navigate to="/co-instance" replace />;
  };

  // ✦ 1. country picker first ✦
  if (showCountryModal && availableCountries.length > 0) {
    const isOrganizationValid =
      organizationSelection !== '' &&
      (organizationSelection !== 'Other' || String(otherOrganizationName || '').trim().length > 0);
    const isTitleValid = String(titleInput || '').trim().length > 0;
    const canSubmitCountryModal = countryModalSelection !== '' && isOrganizationValid && isTitleValid;

    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Select Your Country (Duty Station)</h2>
          <p className="mb-6">We could not determine your country (Duty Station). Please select it from the list below to continue.</p>
          <select
            className="w-full p-2 border border-gray-300 rounded-md mb-6"
            onChange={e => setCountryModalSelection(e.target.value)}
            value={countryModalSelection}
          >
            <option value="" disabled>Select country... / Selectionnez votre pays...</option>
            {availableCountries.map(c => (
              <option key={c.iso3} value={c.Country_Name}>{c.Country_Name}</option>
            ))}
          </select>
          <div className="mb-4 text-left">
            <label className="block mb-2 font-semibold text-gray-700">Organization</label>
            <select
              className="w-full p-2 border border-gray-300 rounded-md"
              value={organizationSelection}
              onChange={e => setOrganizationSelection(e.target.value)}
            >
              <option value="" disabled>Select organization... / Selectionnez votre organisation...</option>
              <option value="UNICEF">UNICEF</option>
              <option value="WHO">WHO</option>
              <option value="Gavi">Gavi</option>
              <option value="MOH">MOH</option>
              <option value="EPI">EPI</option>
              <option value="NLWG">NLWG</option>
              <option value="Other">Other</option>
            </select>
          </div>
          {organizationSelection === 'Other' && (
            <div className="mb-6 text-left">
              <label className="block mb-2 font-semibold text-gray-700">Organization name / Nom de l'organisation</label>
              <input
                type="text"
                className="w-full p-2 border border-gray-300 rounded-md"
                value={otherOrganizationName}
                onChange={e => setOtherOrganizationName(e.target.value)}
                placeholder="Enter organization name / Entrez le nom de l'organisation"
                required
              />
            </div>
          )}
          <div className="mb-6 text-left">
            <label className="block mb-2 font-semibold text-gray-700">Job title / Titre du poste</label>
            <input
              type="text"
              className="w-full p-2 border border-gray-300 rounded-md"
              value={titleInput}
              onChange={e => setTitleInput(e.target.value)}
              placeholder="e.g. Immunization Officer / ex. Agent de vaccination"
              required
            />
          </div>
          <button
            type="button"
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-semibold py-2 px-4 rounded-md"
            disabled={!canSubmitCountryModal}
            onClick={() =>
              handleCountryModalSubmit(
                countryModalSelection,
                'country',
                organizationSelection,
                otherOrganizationName,
                titleInput
              )
            }
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="min-h-screen bg-gray-100">
        <UserEngagementTracker />
        { /* Sign out handled in TopBar now */ }
        <Routes>
          <Route path="/" element={guard('stock-performance', <StockPerformance userClaims={userClaims} />)} />
          <Route path="/stock-performance" element={guard('stock-performance', <StockPerformance userClaims={userClaims} />)} />
          <Route path="/co-instance" element={guard('co-instance', <COInstance userClaims={userClaims} />)} />
          <Route path="/data-warehouse" element={guard('data-warehouse', <DataWarehouse userClaims={userClaims} />)} />
          <Route path="/tracking" element={guard('tracking', <TrackingSystem userClaims={userClaims} />)} />
          <Route path="/projects" element={guard('projects', <Projects userClaims={userClaims} />)} />
          <Route path="/ticketing-system" element={guard('ticketing-system', <TicketSystem userRole="Administrator" userEmail={userClaims.email} userClaims={userClaims} />)} />
          <Route path="/ticket/:id" element={guard('ticketing-system', <TicketPage userClaims={userClaims} />)} />
          <Route path="/co-feedback" element={guard('co-feedback', <COFeedback userClaims={userClaims} />)} />
          <Route path="/resolution-matrices" element={guard('resolution-matrices', <ResolutionMatrices userClaims={userClaims} />)} />
          <Route path="/inventories" element={guard('inventories', <Inventories userClaims={userClaims} />)} />
          <Route path="/ucc-inventories" element={guard('ucc-inventories', <UCCInventories userClaims={userClaims} />)} />
          <Route path="/UCCInventories" element={guard('ucc-inventories', <UCCInventories userClaims={userClaims} />)} />
          <Route path="/temperature-rtmd" element={guard('temperature-rtmd', <TemperatureRTMDs userClaims={userClaims} />)} />
          <Route path="/gaps" element={guard('gaps', <Gaps userClaims={userClaims} />)} />
          <Route path="/fsp4all" element={guard('fsp4all', <FSP4All userClaims={userClaims} />)} />
          <Route path="/fsp4all-forecasting" element={guard('fsp4all-forecasting', <FSP4AllForecasting userClaims={userClaims} />)} />
          <Route path="/drive" element={guard('drive', <DRIVE userClaims={userClaims} />)} />
          <Route path="/scip" element={guard('scip', <SCIP userClaims={userClaims} />)} />
          <Route path="/scip-tool" element={guard('scip', <SCIPTool userClaims={userClaims} />)} />
          <Route path="/scip-tool/:cipId" element={guard('scip', <SCIPToolDetail userClaims={userClaims} />)} />
          <Route path="/evma" element={guard('evma', <EVMA userClaims={userClaims} />)} />
          <Route path="/nlwg-maturity" element={guard('nlwg-maturity', <NLWG userClaims={userClaims} />)} />
          <Route path="/solarization" element={guard('solarization', <Solarization userClaims={userClaims} />)} />
          <Route path="/waste-management" element={guard('waste-management', <WasteManagement userClaims={userClaims} />)} />
          <Route path="/carbon-footprint" element={guard('carbon-footprint', <CarbonFootprint userClaims={userClaims} />)} />
          <Route path="/coverage-data" element={guard('coverage-data', <CoverageData userClaims={userClaims} />)} />
          <Route path="/new-vaccine-intro" element={guard('new-vaccine-intro', <NewVaccineIntro userClaims={userClaims} />)} />
          <Route path="/outbreaks" element={guard('outbreaks', <Outbreaks userClaims={userClaims} />)} />
          <Route path="/budgets" element={guard('budgets', <Budgets userClaims={userClaims} />)} />
          <Route path="/admin" element={guard('admin', <Admin userClaims={userClaims} />)} />
          <Route path="/gavi-reporting-indicators" element={guard('coverage-data', <GAVIReportingIndicators userClaims={userClaims} />)} />
          <Route path="/v2" element={guard('stock-performance', <WebV2Module />)} />
          <Route path="/v2/new-vaccines" element={guard('stock-performance', <WebV2Module initialPlaceholderPage="new-vaccines" />)} />
          <Route path="/v2/demand-vaccine-trust" element={guard('stock-performance', <WebV2Module initialPlaceholderPage="demand-vaccine-trust" />)} />
          <Route path="/v2/drive" element={guard('drive', <WebV2Module initialThriveSubpage="drive" />)} />
          <Route path="/v2/evma" element={guard('evma', <WebV2Module initialThriveSubpage="evma" />)} />
          <Route path="/v2/outbreaks" element={guard('outbreaks', <WebV2Module initialPlaceholderPage="outbreaks" />)} />
          <Route path="/v2/budgets" element={guard('budgets', <WebV2Module initialPlaceholderPage="budgets" />)} />
          <Route path="/v2/scip-tool" element={guard('scip', <WebV2Module initialPlaceholderPage="scip-tool" />)} />
          <Route path="/v2/scip-tool/:cipId" element={guard('scip', <WebV2Module initialPlaceholderPage="scip-tool-detail" />)} />
          <Route path="/v2/management-kpi" element={guardManagementKpi(<WebV2Module initialPlaceholderPage="management-kpi" />)} />
        </Routes>
        <SupportChatbotV2 />
      </div>
    </Router>
  );
}

function App() {
  const [msalInstance, setMsalInstance] = useState(null);
  const [initError, setInitError] = useState(null);
  // Maintain user state for context consumers
  const [user, setUser] = useState({ email: '', name: '', lastName: '', country: '' });

  useEffect(() => {
    const initializeMsal = async () => {
      try {
        const instance = await authService.getMsalInstance();
        setMsalInstance(instance);
      } catch (error) {
        console.error('Failed to initialize MSAL:', error);
        setInitError(error);
      }
    };
    initializeMsal();
  }, []);

  if (initError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-red-600">Failed to initialize authentication. Please try refreshing the page.</div>
      </div>
    );
  }

  if (!msalInstance) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Initializing authentication...</div>
      </div>
    );
  }

  // Check for public guest route early
  const path = window.location.pathname;
  const isGuestRoute = path.startsWith('/public/ticket') || path.startsWith('/guest/ticket');

  if (isGuestRoute) {
    return (
      <Router>
        <Routes>
          <Route path="/public/ticket/:token" element={<GuestTicketPage />} />
          <Route path="/guest/ticket/:token" element={<GuestTicketPage />} />
        </Routes>
      </Router>
    );
  }

  return (
    <Sentry.ErrorBoundary fallback={<p>An error has occurred. You can contact our team thrive360[at]unicef.org</p>}>
      <MsalProvider instance={msalInstance}>
        <UserContext.Provider value={{ user, setUser }}>
          <AuthenticatedApp setUser={setUser} />
        </UserContext.Provider>
      </MsalProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;